/**
 * Conversation Service
 * Real API implementation for AI Labs
 */

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://z6zx064ldk.execute-api.us-east-1.amazonaws.com/prod';

/**
 * 현재 로그인한 사용자 ID 가져오기 (Cognito sub)
 */
const getCurrentUserId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo');
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      const userId = userInfo.userId || userInfo.sub || null;
      if (userId) return userId;
    }
  } catch (error) {
    console.warn('[ConversationService] Failed to get user ID:', error);
  }
  // Env var fallback (dev)
  const fallbackId = import.meta.env.VITE_USER_ID || null;
  if (fallbackId) return fallbackId;

  // Anonymous user: reuse stable ID from localStorage
  let anonId = localStorage.getItem('anonUserId');
  if (!anonId) {
    anonId = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('anonUserId', anonId);
  }
  return anonId;
};

/**
 * API fetch wrapper with error handling
 */
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const userId = getCurrentUserId();

  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(userId && { 'x-user-id': userId })
  };

  // API 요청 로깅
  console.log('%c[API] 요청', 'color: #3b82f6; font-weight: bold;', {
    endpoint,
    method: options.method || 'GET',
    userId: userId || '(없음 - 인증 실패 가능)'
  });

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('%c[API] ❌ 응답 실패', 'color: #ef4444;', {
        endpoint,
        status: response.status,
        error: data.error
      });
      throw new Error(data.error || `API Error: ${response.status}`);
    }

    console.log('%c[API] ✅ 응답 성공', 'color: #22c55e;', { endpoint, hasData: !!data.data });
    return data;
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
};

export const conversationService = {
  /**
   * Get all conversations
   * @returns {Promise<Array>}
   */
  async getConversations() {
    try {
      const result = await apiFetch('/conversations');
      return result.data?.conversations || [];
    } catch (error) {
      console.error('Failed to get conversations:', error);
      return [];
    }
  },

  /**
   * Get a single conversation by ID
   * @param {string} id - Conversation ID
   * @returns {Promise<Object|null>}
   */
  async getConversationById(id) {
    console.log('%c[CONV] 대화 조회 시도', 'color: #f59e0b; font-weight: bold;', { conversationId: id });
    try {
      const result = await apiFetch(`/conversations/${id}`);
      // API returns data directly, not data.conversation
      const conversation = result.data || null;
      if (conversation) {
        console.log('%c[CONV] ✅ 대화 찾음', 'color: #22c55e;', {
          id: conversation.id,
          title: conversation.title,
          messageCount: conversation.messages?.length || 0
        });
      } else {
        console.warn('%c[CONV] ⚠️ 대화 없음 (null 반환)', 'color: #f59e0b;');
      }
      return conversation;
    } catch (error) {
      console.error('%c[CONV] ❌ 대화 조회 실패', 'color: #ef4444;', { id, error: error.message });
      return null;
    }
  },

  /**
   * Create a new conversation
   * @param {Object} data - Conversation data
   * @returns {Promise<Object>}
   */
  async createConversation(data) {
    const result = await apiFetch('/conversations', {
      method: 'POST',
      body: JSON.stringify({
        title: data.title || '새 대화',
        lastTool: data.lastTool || null
      })
    });
    return result.data?.conversation || result.data;
  },

  /**
   * Update a conversation
   * @param {string} id - Conversation ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>}
   */
  async updateConversation(id, updates) {
    const result = await apiFetch(`/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    return result.data?.conversation || result.data;
  },

  /**
   * Update conversation title
   * @param {string} id - Conversation ID
   * @param {string} title - New title
   * @returns {Promise<Object>}
   */
  async updateTitle(id, title) {
    return this.updateConversation(id, { title });
  },

  /**
   * Add message to conversation
   * @param {string} conversationId - Conversation ID
   * @param {Object} message - Message object
   * @returns {Promise<Object>}
   */
  async addMessage(conversationId, message) {
    // The backend stores messages separately in ai-labs-messages table
    // For now, we'll update the conversation's lastTool and messageCount
    try {
      // If conversation doesn't exist, create it first
      let conversation = await this.getConversationById(conversationId);

      if (!conversation) {
        conversation = await this.createConversation({
          id: conversationId,
          title: message.content?.substring(0, 30) || '새 대화',
          lastTool: message.tool || null
        });
        conversationId = conversation.id;
      }

      // Update conversation metadata
      const updates = {
        lastTool: message.tool || conversation.lastTool
      };

      // Auto-update title from first user message
      if (message.role === 'user' && (!conversation.messageCount || conversation.messageCount === 0)) {
        updates.title = message.content.substring(0, 30) +
          (message.content.length > 30 ? '...' : '');
      }

      await this.updateConversation(conversationId, updates);

      // Return message with generated ID (actual storage is handled by WebSocket)
      return {
        id: `msg_${Date.now()}`,
        timestamp: new Date().toISOString(),
        ...message
      };
    } catch (error) {
      console.error('Failed to add message:', error);
      // Return message anyway for local state
      return {
        id: `msg_${Date.now()}`,
        timestamp: new Date().toISOString(),
        ...message
      };
    }
  },

  /**
   * Delete a conversation
   * @param {string} id - Conversation ID
   * @returns {Promise<boolean>}
   */
  async deleteConversation(id) {
    const result = await apiFetch(`/conversations/${id}`, {
      method: 'DELETE'
    });
    return result.success || false;
  },

  /**
   * Search conversations by query
   * @param {string} query - Search query
   * @returns {Promise<Array>}
   */
  async searchConversations(query) {
    try {
      const result = await apiFetch(`/conversations/search?q=${encodeURIComponent(query)}`);
      return result.data?.conversations || [];
    } catch (error) {
      console.error('Failed to search conversations:', error);
      return [];
    }
  },

  /**
   * Get user ID
   * @returns {string|null}
   */
  getUserId() {
    return getCurrentUserId();
  }
};

export default conversationService;
